import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Form } from "@/components/ui/form";
import { Diff } from "@/features/FormCard/Diff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { useMemo } from "react";

import { ExternalLink } from "@/components/ExternalLink";
import { Label } from "@radix-ui/react-label";
import { dynamicFormConfig } from "./edit-form-conf";
import { TechnicalInfoCardStatic } from "./technical_info_card";
import { getFieldTranslations } from "./translations";
import { useTranslation } from "react-i18next";

export function A6StaticDatumsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    optionsMap,
    handleDelete,
    selectFieldsConfig,
    isLoadingTranslations,
    options,
    fetchedData,
  } = useDynamicForm({
    id,
    client,
    table_name,
    formConfig: dynamicFormConfig,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "concentrated_asset") {
        return {
          text: value,
        };
      } else if (key === "a6_issuer_name") {
        return {
          text: fetchedData.a6_issuer_name,
          id: fetchedData.issuer_id,
        };
      }
      return value;
    },
  });

  const currencyOptions = useCurrencyOptions();

  const name = form.watch("name");
  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch("asset_class_2");
  const fieldTranslations = getFieldTranslations(t);

  const translatedDynamicConfig = useMemo(() => {
    const cfg = selectFieldsConfig?.asset_class_2;
    if (!cfg) return undefined;

    const translatedSections = Object.fromEntries(
      Object.entries(cfg.section).map(([sectionName, fields]) => [
        sectionName,
        Object.fromEntries(
          Object.entries(fields).map(([fieldName, fieldConfig]) => [
            fieldName,
            {
              ...fieldConfig,
              label: t(`a6_static_data.columns.${fieldName}`, {
                ns: "tables",
                defaultValue: fieldConfig.label,
              }),
            },
          ]),
        ),
      ]),
    );

    return {
      ...cfg,
      section: translatedSections,
    };
  }, [selectFieldsConfig?.asset_class_2, t]);

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="font-bold text-gray-700">
                  {t("a6_static_data.title", {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t("a6_static_data.sections.general_information", {
                      ns: "tables",
                      defaultValue: "Informations générales",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-8">
                      <InputField
                        name="name"
                        label={t("a6_static_data.columns.name", {
                          ns: "tables",
                          defaultValue: "Nom",
                        })}
                        isRequired={true}
                        capitalize={true}
                        control={form.control}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <AutocompleteLabelField
                        name="currency"
                        label={t("a6_static_data.columns.currency", {
                          ns: "tables",
                          defaultValue: "Devise",
                        })}
                        options={currencyOptions}
                        isRequired={true}
                        control={form.control}
                        className="min-w-0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    <InputField
                      name="isin"
                      label={t("a6_static_data.columns.isin", {
                        ns: "tables",
                        defaultValue: "ISIN",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="code"
                      label={t("a6_static_data.columns.code", {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="ticker"
                      label={t("a6_static_data.columns.ticker", {
                        ns: "tables",
                        defaultValue: "Ticker",
                      })}
                      control={form.control}
                    />
                  </div>
                  <AutocompleteAsyncField
                    name="a6_issuer_name"
                    label={t("a6_static_data.columns.a6_issuer_name", {
                      ns: "tables",
                      defaultValue: "Émetteur",
                    })}
                    control={form.control}
                    base_url="a6/a6_issuers"
                    id_column="issuer_id"
                    column_name="name"
                  />
                  <SelectFieldWithSection
                    listboxWrapperClassNames="!h-[400px] !max-h-none"
                    primary_key="asset_class_1"
                    name="asset_class_2"
                    label={t("a6_static_data.columns.asset_class_2", {
                      ns: "tables",
                      defaultValue: "Classe d'actifs",
                    })}
                    control={form.control}
                    sections={optionsMap.asset_class_2}
                    placeholder={t("form.placeholders.select")}
                    isRequired
                  />
                  <DynamicFields
                    primaryValue={assetClass1}
                    fieldValue={assetClass2}
                    control={form.control}
                    config={translatedDynamicConfig}
                    isLoadingTranslations={isLoadingTranslations}
                    options={options}
                  />
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-[3.20rem] right-12 2xl:right-10 lg:flex gap-2 flex-row hidden">
              {fetchedData?.has_transactions && (
                <Button
                  href={toWorkspacePath("/admin/a6/a6_transactions")}
                  queryParams={{ a6_static_data_id_equal: id }}
                  variant="bordered"
                  color="primary"
                  showAnchorIcon
                  as={ExternalLink}
                  radius="full"
                  className="bg-white border-1 border-neutral-300 shadow-xl"
                >
                  {t("actions.transactions")}
                </Button>
              )}
              {fetchedData?.has_time_series && (
                <Button
                  href={toWorkspacePath("/admin/a6/a6_time_series")}
                  queryParams={{ a6_static_data_id_equal: id }}
                  variant="bordered"
                  color="primary"
                  showAnchorIcon
                  as={ExternalLink}
                  radius="full"
                  className="bg-white border-1 border-neutral-300 shadow-xl"
                >
                  {t("actions.valuations", { defaultValue: "Valorisations" })}
                </Button>
              )}
              {form.watch("a6_issuer_name")?.id && (
                <Button
                  href={toWorkspacePath(
                    `/admin/a6/a6_issuers/${form.watch("a6_issuer_name")?.id}`,
                  )}
                  variant="bordered"
                  color="primary"
                  showAnchorIcon
                  as={ExternalLink}
                  radius="full"
                  className="bg-white border-1 border-neutral-300 shadow-xl"
                >
                  {t("a6_static_data.columns.a6_issuer_name", {
                    ns: "tables",
                    defaultValue: "Émetteur",
                  })}
                </Button>
              )}
            </div>
          )}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(
                    changedFields,
                    ["issuer_id"],
                    fieldTranslations,
                    table_name,
                  )
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <TechnicalInfoCardStatic
        id={Number(id)}
        table_name={table_name}
        strategy_id_control={form.control}
        client={client}
      />
    </>
  );
}
